import {
  Toolbar,
  IconButton,
  Typography,
  Avatar,
  Menu,
  Divider,
  ListItemIcon,
  List,
  ListItem,
  ListItemText,
  Button,
  CircularProgress,
  Slide,
  Tooltip,
  useMediaQuery,
  useTheme,
  alpha,
} from "@mui/material";
import MuiDrawer from "@mui/material/Drawer";
import MuiAppBar from "@mui/material/AppBar";
import MenuIcon from "@mui/icons-material/Menu";
import { Box, Stack } from "@mui/system";
import {
  AdminPanelSettings,
  ChevronLeft,
  Fence,
  Handshake,
  IntegrationInstructions,
  Logout,
  ManageAccounts,
  Person,
  Security,
  Storage,
  LightMode,
  DarkMode,
} from "@mui/icons-material";
import { styled } from "@mui/material/styles";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { NavLink } from "react-router-dom";
import { useContext, useEffect, useState } from "react";
import logo from "../utils/images/ridePlus.png";
import useAxiosPrivate from "../hooks/useAxiosPrivate";
import { useMutation, useQuery } from "react-query";
import { useSnackbar } from "notistack";
import AuthContext from "../context/AuthProvider";
import { PROFILE_IMAGE_BASE_URL, PROFILE_IMAGE_URL } from "../utils/config";
import { queryClient } from "../App";
import useUserPermissions from "../hooks/useUserPermissions";
import AccountSwitcher from "../components/AccountSwitcher";
import { useThemeMode } from "../context/ThemeContext";
import useTenantBranding from "../hooks/useTenantBranding";

const drawerWidth = 260;

const openedMixin = (theme) => ({
  width: drawerWidth,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: "hidden",
  borderRight: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.paper,
});

const closedMixin = (theme) => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: "hidden",
  width: `calc(${theme.spacing(8)} + 1px)`,
  [theme.breakpoints.up("sm")]: {
    width: `calc(${theme.spacing(9)} + 1px)`,
  },
  borderRight: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.paper,
});

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: theme.spacing(0, 1),
  minHeight: "56px !important",
  ...theme.mixins.toolbar,
}));

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  zIndex: theme.zIndex.drawer + 1,
  height: 56,
  backgroundColor: alpha(theme.palette.background.paper, 0.8),
  backdropFilter: "blur(10px)",
  boxShadow: "none",
  borderBottom: `1px solid ${theme.palette.divider}`,
  color: theme.palette.text.primary,
  transition: theme.transitions.create(["width", "margin"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  // Only offset the bar when the permanent drawer is visible (md+). On mobile the
  // drawer is temporary/full-screen; applying margin here left a blank strip and
  // squeezed the toolbar.
  ...(open && {
    [theme.breakpoints.up("md")]: {
      marginLeft: drawerWidth,
      width: `calc(100% - ${drawerWidth}px)`,
      transition: theme.transitions.create(["width", "margin"], {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
      }),
    },
  }),
}));

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  ...(open && {
    ...openedMixin(theme),
    "& .MuiDrawer-paper": openedMixin(theme),
  }),
  ...(!open && {
    ...closedMixin(theme),
    "& .MuiDrawer-paper": closedMixin(theme),
  }),
}));

function Main() {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();

  const { enqueueSnackbar } = useSnackbar();
  const axiosPrivate = useAxiosPrivate();
  const {
    auth,
    setAuth,
    setPermissions,
    setRole,
    removeTenantSession,
    clearTenantContext,
    tenantSessions,
    currentTenant,
    clearAllTenantSessions,
    switchToTenant,
  } = useContext(AuthContext);

  const { mode, toggleTheme } = useThemeMode();
  const { tenantLogo, hideDefaultBranding } = useTenantBranding();

  const {
    authMethodsPage,
    clientsPage,
    identityProvidersPages,
    resourceServersPage,
    rolesPage,
    scopesPage,
    usersPage,
  } = useUserPermissions();

  const [mobileOpen, setMobileOpen] = useState(false);
  const [open, setOpen] = useState(true);
  const [anchorEl, setAnchorEl] = useState(null);
  const openMenu = Boolean(anchorEl);

  const isMdUp = useMediaQuery(theme.breakpoints.up("md"));
  /** Mobile drawer should always show labels; desktop mini-drawer follows `open`. */
  const drawerLabelsExpanded = isMdUp ? open : true;

  const tenantSession = tenantSessions[tenantSessions.length - 1];

  const {
    isLoading: profileLoading,
    data: profile,
    refetch,
  } = useQuery(["profile"], () => axiosPrivate.get(`/profile`), {
    refetchOnWindowFocus: false,
  });

  const { mutate: mutateSignOut, isLoading: signOutIsLoading } = useMutation(
    (refreshToken) =>
      axiosPrivate.post(
        "/logout",
        { refresh_token: refreshToken },
        { withCredentials: true },
      ),
    {
      onSuccess: () => {
        if (tenantSession?.tenant_id)
          removeTenantSession(tenantSession.tenant_id);
      },
    },
  );

  const { data: permissionsData, refetch: refetchPermissions } = useQuery(
    ["user-permission"],
    () => axiosPrivate.get(`/profile/permissions`),
    { refetchOnWindowsFocus: false },
  );

  const signOutAll = async (callback) => {
    const refreshToken = auth.refresh_token;
    return mutateSignOut(refreshToken, {
      onSuccess: () => {
        clearAllTenantSessions();
        setAuth({});
        localStorage.setItem("persist", false);
        clearTenantContext(true);
        queryClient.clear();
        if (callback) callback();
      },
    });
  };

  const signOutCurrentTenant = async () => {
    const currentTenantId = currentTenant?.id || tenantSession?.tenant_id;
    if (!currentTenantId) return signOutAll(() => navigate("/login"));

    const currentSession =
      tenantSessions.find((s) => s.tenant_id === currentTenantId) ||
      tenantSession;
    const refreshToken =
      currentSession?.auth?.refresh_token || auth.refresh_token;
    const remainingSessions = tenantSessions.filter(
      (s) => s.tenant_id !== currentTenantId,
    );

    return mutateSignOut(refreshToken, {
      onSuccess: () => {
        removeTenantSession(currentTenantId);
        if (remainingSessions.length > 0) {
          const next = remainingSessions[remainingSessions.length - 1];
          switchToTenant(next.tenant_id, false);
          queryClient.clear();
        } else {
          clearAllTenantSessions();
          setAuth({});
          localStorage.setItem("persist", false);
          clearTenantContext(true);
          queryClient.clear();
          navigate("/login");
        }
      },
    });
  };

  const handleDrawerToggle = () => setOpen(!open);
  const handleMobileToggle = () => setMobileOpen(!mobileOpen);
  const handleClick = (e) => setAnchorEl(e.currentTarget);
  const handleClose = () => {
    setAnchorEl(null);
    refetch();
  };

  const LinkItem = ({ to, icon, text }) => {
    const active = to === location.pathname;
    return (
      <ListItem
        component={NavLink}
        to={to}
        onClick={() => setMobileOpen(false)}
        sx={{
          mb: 0.5,
          mx: 1,
          width: "auto",
          borderRadius: 3,
          color: active ? "primary.main" : "text.secondary",
          backgroundColor: active
            ? alpha(theme.palette.primary.main, 0.08)
            : "transparent",
          "&:hover": {
            backgroundColor: active
              ? alpha(theme.palette.primary.main, 0.12)
              : "rgba(0,0,0,0.04)",
          },
          px: 2,
          py: 1,
          justifyContent: drawerLabelsExpanded ? "initial" : "center",
        }}
      >
        <ListItemIcon
          sx={{
            minWidth: 0,
            mr: drawerLabelsExpanded ? 2 : "auto",
            justifyContent: "center",
            color: active ? "primary.main" : "inherit",
          }}
        >
          <Tooltip
            title={text}
            arrow
            placement="right"
            disableHoverListener={drawerLabelsExpanded}
          >
            {icon}
          </Tooltip>
        </ListItemIcon>
        <ListItemText
          primary={text}
          sx={{
            opacity: drawerLabelsExpanded ? 1 : 0,
            "& .MuiTypography-root": {
              fontWeight: active ? 800 : 500,
              fontSize: "0.875rem",
            },
          }}
        />
      </ListItem>
    );
  };

  const ListItems = [
    {
      link: "/users",
      permission: usersPage,
      icon: <Person fontSize="small" />,
      text: "Users",
    },
    {
      link: "/roles",
      permission: rolesPage,
      icon: <ManageAccounts fontSize="small" />,
      text: "Roles",
    },
    {
      link: "/scopes",
      permission: scopesPage,
      icon: <Fence fontSize="small" />,
      text: "Scopes",
    },
    {
      link: "/clients",
      permission: clientsPage,
      icon: <Handshake fontSize="small" />,
      text: "Clients",
    },
    {
      link: "/resource-servers",
      permission: resourceServersPage,
      icon: <Storage fontSize="small" />,
      text: "Resource Servers",
    },
    {
      link: "/identity-providers",
      permission: identityProvidersPages,
      icon: <Security fontSize="small" />,
      text: "Identity Providers",
    },
    {
      link: "/tenants/auth-methods",
      permission: authMethodsPage,
      icon: <AdminPanelSettings fontSize="small" />,
      text: "Auth Methods",
    },
  ];

  const hasAdministrationNav = ListItems.some((item) => item.permission);

  const drawerContent = (
    <Box>
      <DrawerHeader>
        {(tenantLogo || !hideDefaultBranding) && (
          <Avatar
            variant="square"
            sx={{ height: 32, width: "auto", transition: "all 0.3s" }}
            src={tenantLogo || logo}
          />
        )}
      </DrawerHeader>
      <Divider sx={{ opacity: 0.5 }} />
      <List sx={{ pt: 2 }}>
        <LinkItem to="/" icon={<Person fontSize="small" />} text="My Profile" />
        <LinkItem
          to="/authorized-apps"
          icon={<IntegrationInstructions fontSize="small" />}
          text="Authorized Apps"
        />
        {hasAdministrationNav && (
          <Box sx={{ px: 3, pt: 2, pb: 1 }}>
            <Typography
              variant="overline"
              sx={{
                display: drawerLabelsExpanded ? "block" : "none",
                fontWeight: 800,
                color: "text.disabled",
                letterSpacing: "1px",
              }}
            >
              Administration
            </Typography>
            {!drawerLabelsExpanded && <Divider sx={{ my: 1 }} />}
          </Box>
        )}
        {ListItems.map(
          (item, idx) =>
            item.permission && (
              <LinkItem
                key={idx}
                to={item.link}
                icon={item.icon}
                text={item.text}
              />
            ),
        )}
      </List>
    </Box>
  );

  useEffect(() => {
    if (profile?.data?.data?.role) {
      setRole(profile?.data?.data?.role);
      refetchPermissions();
    }
  }, [profile?.data?.data?.role, refetchPermissions, setRole]);

  useEffect(() => {
    if (permissionsData) setPermissions(permissionsData?.data?.data);
  }, [permissionsData, setPermissions]);

  return (
    <Box
      sx={{
        display: "flex",
        bgcolor: "background.default",
        minHeight: "100vh",
      }}
    >
      <AppBar position="fixed" open={open}>
        <Toolbar
          sx={{
            minHeight: "56px !important",
            px: { xs: 1.5, sm: 3 },
            gap: { xs: 1, sm: 2 },
          }}
        >
          <IconButton
            color="inherit"
            aria-label="open drawer"
            onClick={handleDrawerToggle}
            edge="start"
            sx={{
              mr: { xs: 0, md: 2 },
              display: { xs: "none", md: "flex" },
              bgcolor: "action.hover",
              borderRadius: 2,
              flexShrink: 0,
            }}
          >
            {open ? <ChevronLeft /> : <MenuIcon />}
          </IconButton>
          <IconButton
            color="inherit"
            aria-label="open navigation menu"
            onClick={handleMobileToggle}
            edge="start"
            sx={{
              mr: { xs: 0, md: 2 },
              display: { xs: "flex", md: "none" },
              flexShrink: 0,
            }}
          >
            <MenuIcon />
          </IconButton>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              minWidth: 0,
              flex: 1,
            }}
          >
            <Typography
              variant="subtitle2"
              noWrap
              sx={{
                fontWeight: 800,
                color: "text.secondary",
                textTransform: "uppercase",
                letterSpacing: { xs: "0.5px", sm: "1px" },
                fontSize: { xs: "10px", sm: "11px" },
              }}
            >
              Management Console
            </Typography>
          </Box>

          <Stack
            direction="row"
            spacing={{ xs: 0.5, sm: 1.5 }}
            alignItems="center"
            sx={{ flexShrink: 0 }}
          >
            <Tooltip
              title={`Switch to ${mode === "light" ? "dark" : "light"} mode`}
            >
              <IconButton
                onClick={toggleTheme}
                sx={{ bgcolor: "action.hover", borderRadius: 2 }}
              >
                {mode === "light" ? (
                  <DarkMode fontSize="small" />
                ) : (
                  <LightMode fontSize="small" />
                )}
              </IconButton>
            </Tooltip>
            {/* <Box sx={{ display: { xs: 'none', sm: 'block' }, textAlign: 'right' }}>
              <Typography variant="body2" sx={{ fontWeight: 800, lineHeight: 1 }}>{profile?.data?.data?.first_name}</Typography>
              <Typography variant="caption" sx={{ color: 'text.disabled', fontWeight: 600 }}>Administrator</Typography>
            </Box> */}
            <IconButton
              onClick={handleClick}
              sx={{ p: 0.5, border: "1px solid rgba(0,0,0,0.05)" }}
            >
              <Avatar
                sx={{ width: 34, height: 34, borderRadius: 2 }}
                src={`${PROFILE_IMAGE_BASE_URL}/${PROFILE_IMAGE_URL}/${profile?.data?.data?.profile_picture}`}
              />
            </IconButton>
          </Stack>

          <Menu
            anchorEl={anchorEl}
            open={openMenu}
            onClose={handleClose}
            onClick={handleClose}
            slotProps={{
              paper: {
                elevation: 0,
                sx: {
                  borderRadius: 4,
                  mt: 1.5,
                  width: 280,
                  boxShadow: "0 10px 40px rgba(0,0,0,0.1)",
                  border: "1px solid rgba(0,0,0,0.05)",
                },
              },
            }}
            transformOrigin={{ horizontal: "right", vertical: "top" }}
            anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
          >
            <Box
              sx={{
                p: 3,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              {profileLoading ? (
                <CircularProgress size={24} />
              ) : (
                <>
                  <Avatar
                    sx={{
                      width: 80,
                      height: 80,
                      mb: 2,
                      borderRadius: 4,
                      boxShadow: "0 8px 20px rgba(0,0,0,0.1)",
                    }}
                    src={`${PROFILE_IMAGE_BASE_URL}/${PROFILE_IMAGE_URL}/${profile?.data?.data?.profile_picture}`}
                  />
                  <Typography sx={{ fontWeight: 900, mb: 0.5 }}>
                    {profile?.data?.data?.first_name}{" "}
                    {profile?.data?.data?.last_name}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: "text.disabled", fontWeight: 700 }}
                  >
                    {profile?.data?.data?.email}
                  </Typography>
                </>
              )}
            </Box>
            <Divider sx={{ opacity: 0.5 }} />
            {tenantSessions?.length > 0 && (
              <Box sx={{ py: 1 }}>
                <AccountSwitcher
                  onClose={handleClose}
                  phone={profile?.data?.data?.phone}
                />
              </Box>
            )}
            <Divider sx={{ opacity: 0.5 }} />
            <Box
              sx={{ p: 2, display: "flex", flexDirection: "column", gap: 1 }}
            >
              <Button
                fullWidth
                size="small"
                variant="outlined"
                color="inherit"
                onClick={signOutCurrentTenant}
                sx={{ borderRadius: 2, textTransform: "none", fontWeight: 700 }}
              >
                Logout from Tenant
              </Button>
              <Button
                fullWidth
                size="small"
                variant="contained"
                color="error"
                onClick={() => signOutAll(() => navigate("/login"))}
                startIcon={<Logout />}
                sx={{ borderRadius: 2, textTransform: "none", fontWeight: 700 }}
              >
                Logout All
              </Button>
            </Box>
          </Menu>
        </Toolbar>
      </AppBar>

      <Box component="nav">
        <MuiDrawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleMobileToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: "block", md: "none" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: drawerWidth,
              borderRadius: "0 24px 24px 0",
            },
          }}
        >
          {drawerContent}
        </MuiDrawer>
        <Drawer
          variant="permanent"
          open={open}
          sx={{ display: { xs: "none", md: "block" } }}
        >
          {drawerContent}
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 1.5, sm: 2, md: 4 },
          position: "relative",
          minWidth: 0,
        }}
      >
        <Toolbar sx={{ minHeight: "56px !important" }} />
        <Box
          sx={{
            borderRadius: { xs: 2, sm: 4, md: 6 },
            overflow: { xs: "visible", md: "hidden" },
          }}
        >
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
}

export default Main;
